import moment from 'moment'
import { call, select, put } from 'redux-saga/effects'

import DEFAULT from 'core/data-model/constants/defaults'
import { _validateBirthdate } from 'core/service/insured/validation'
import { partnerApiUtils } from 'core/service-wrapper/partner-api/handler-request/service-utils'
import { partnerRequesting } from 'quick-quote/actions'
import { getToggles } from 'quick-quote/feature-toggles'
import { updateBirthdate, updatePayerBirthdate } from 'quick-quote/insured-information/actions'
import { checkIfUnderage, validatePayerRelationWithAgeChange } from 'quick-quote/insured-information/sagas'
import { getAge, getBirthdate, getPayerAge, getPayerBirthdate } from 'quick-quote/insured-information/selectors'
import { getSelectedDisplayProduct } from 'quick-quote/product-selection/selectors'
import { getPartnerCurrentSelectedProduct } from 'quick-quote/selectors/partner-selector'

/**
 * createInsuredHandler
 * @param productCommonService {ReturnType<import('core/service-wrapper/partner-api/handler/product-common').createProductCommonService>}
 */
export function createInsuredInformationHandler(productCommonService) {
  function* updateProductPackage() {
    const { packageCategory, packageCode } = yield select(getPartnerCurrentSelectedProduct)
    const comingDisplayProduct = yield select(getSelectedDisplayProduct)
    yield call(
      productCommonService.getProductPackageBySelectedProduct,
      packageCategory,
      packageCode,
      comingDisplayProduct
    )
  }

  // insured section

  const insured = {
    /**
     * onEditBirthdate
     * @param action {{ birthdate, forceUpdate }}
     */
    onEditBirthdate: function*(action) {
      yield call(insured.patchEditBirthdate, action, updateBirthdate)
    },
    /**
     * onEditBirthdate
     * @param action {{ birthdate, forceUpdate }}
     * @param updateOrPatchFunc {Function}
     * @param option {{ birthdate, error, age } | undefined}
     */
    patchEditBirthdate: function*(action, updateOrPatchFunc, option) {
      const { birthdate, forceUpdate } = action

      const birthdateState = yield select(getBirthdate)
      const previousAge = yield select(getAge)
      const selectedDisplayProduct = yield select(getSelectedDisplayProduct)
      const current = moment().format('DD/MM/YYYY')

      if (!partnerApiUtils.birthdate.isEquals(birthdateState, birthdate) || forceUpdate) {
        yield put(partnerRequesting('update_birthdate'))
        const validateResult =
          option ?? _validateBirthdate(birthdate, selectedDisplayProduct.entryAge, previousAge, current, false)

        yield put(updateOrPatchFunc(validateResult.birthdate, validateResult.error, validateResult.age))
        yield call(checkIfUnderage, previousAge, validateResult.age)

        if (getToggles().ENABLE_NEW_PAYER_SELECTION) {
          yield call(validatePayerRelationWithAgeChange, previousAge, validateResult.age)
        }
      }
    },
    /**
     * onSelectNatureOfDuty
     * @returns {Generator<*, void, *>}
     */
    onSelectNatureOfDuty: function*(action) {
      yield put(partnerRequesting('change_nature_of_duty'))
      yield call(updateProductPackage)
    },
    /**
     * validateOtherOccupation
     * @returns {Generator<*, void, *>}
     */
    onSelectOtherNatureOfDuty: function*() {
      // validate by call request calculationWithValidation
      if (getToggles().VALIDATE_OTHER_OCCUPATION) {
        // ignored
      }
    },
  }

  // payer section

  const payer = {
    /**
     * onUpdatePayerNeed
     * @param _action {{ payload }}
     */
    onUpdatePayerNeed: function*(_action) {
      yield put(partnerRequesting('change_payer_need'))
      yield call(updateProductPackage)
    },
    /**
     * onEditBirthdate
     * @param action {{ birthdate }}
     */
    onEditBirthdate: function*(action) {
      yield call(payer.patchBirthdate, action, updatePayerBirthdate)
    },
    /**
     * patchBirthdate
     * @param action {{ birthdate }}
     * @param updateOrPatchFunc {Function}
     * @param option {{ birthdate, error, age } | undefined}
     */
    patchBirthdate: function*(action, updateOrPatchFunc, option) {
      const { birthdate } = action

      const birthdateState = yield select(getPayerBirthdate)
      const ageState = yield select(getPayerAge)
      const currentDate = moment().format('DD/MM/YYYY')

      const validateResult =
        option ?? _validateBirthdate(birthdate, DEFAULT.DEFAULT_PAYER_ENTRY_AGE, ageState, currentDate, true)

      if (!partnerApiUtils.birthdate.isEquals(birthdateState, birthdate)) {
        yield put(partnerRequesting('update_payer_birthdate'))
        yield put(updateOrPatchFunc(validateResult.birthdate, validateResult.error, validateResult.age))
      }
    },
    /**
     * onSelectNatureOfDuty
     * @returns {Generator<*, void, *>}
     */
    onSelectNatureOfDuty: function*() {
      yield put(partnerRequesting('change_payer_nature_of_duty'))
      yield call(updateProductPackage)
    },
    /**
     * validateOtherOccupation
     * same as validateOccupation
     * @returns {Generator<*, void, *>}
     */
    onSelectOtherNatureOfDuty: function*() {
      // validate by call request calculationWithValidation
      // TODO - ignore verification
    },
  }

  return {
    insured,
    payer,
  }
}
